import React, { useState, useEffect, useMemo } from 'react';
import { 
  Box, Typography, FormControl, Select, MenuItem, Table, TableBody, 
  TableCell, TableContainer, TableHead, TableRow, Paper, InputBase, Button, InputLabel
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import _ from 'lodash';

const useStyles = makeStyles({
  container: { padding: '12px', backgroundColor: '#ffffff'},
  headerTitle: { fontWeight: 'bold', color: '#000000', marginBottom: '20px' },
  searchArea: { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' },
  yearSelect: { 
    height: '36px', width: '120px', backgroundColor: '#fff',
    '& .MuiSelect-select': { padding: '8px 14px', fontWeight: 400 ,  fontSize: '1rem !important' } 
  },
  tableContainer: { 
    borderRadius: '1px', backgroundColor: '#f8f9fa', border: '1px solid #e2e8f0', 
    boxShadow: '0 4px 12px rgba(0,0,0,0.03)', overflow: 'hidden' 
  },
    table: {
    width: '100%', 
    borderCollapse: 'collapse', 
    tableLayout: 'fixed',
    backgroundColor: '#ffffff',
    '& .MuiTableCell-root': {
      borderBottom: '1px solid rgba(224, 224, 224, 1) !important', 
      borderRight: '1px solid rgba(224, 224, 224, 1) !important',  
      padding: '12px 16px !important', 
      fontSize: '0.875rem !important', 
      fontFamily: 'Pretendard, sans-serif !important',
      backgroundColor: '#ffffff', 
    },

    // [추가] 0번 로우 (데이터 영역의 첫 번째 행) 전체 배경색 설정
    '& tbody tr:nth-of-type(0) .MuiTableCell-root': {
      backgroundColor: '#fff9db !important', // 원하시는 색상(예: 연한 노란색)으로 변경 가능
    },

    // [기존 유지] 모든 행의 첫 번째 컬럼(열)은 회색
    '& .MuiTableCell-root:first-child': {
      backgroundColor: '#f5f5f5 !important',
      fontWeight: '600 !important',
      textAlign: 'center !important',
    },
    '& tbody tr:nth-of-type(1) .MuiTableCell-root:first-child': {
      backgroundColor: '#f5f5f5 !important', 
    },

    '& .MuiTableCell-root:last-child': {
      borderRight: 'none !important',
    },
  },

  headerCell: { 
    backgroundColor: '#e3f2fd !important', 
    color: '#000000 !important', 
    fontWeight: '600 !important', 
    textAlign: 'center !important',
    borderBottom: '2px solid #bbdefb !important',
    '&:first-child': {
      backgroundColor: '#f5f5f5 !important',
      borderBottom: '2px solid #e0e0e0 !important',
    }
  },
    sumRow: { 
    '& .MuiTableCell-root': { 
      backgroundColor: '#edf2f7 !important',
      color: '#000000 !important',
      fontWeight: '700 !important',
      borderBottom: '1px solid rgba(224, 224, 224, 1) !important'
    },
    '& .MuiTableCell-root:first-child': {
      backgroundColor: '#edf2f7 !important',
      color: '#000000 !important'
    }
  },
  inputCell: { padding: '4px 10px !important' },
  inputBase: {
    backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '6px', width: '100%',
    '& input': { 
      padding: '6px 26px !important', fontSize: '0.875rem', fontWeight: '600',
      textAlign: 'right', fontVariantNumeric: 'tabular-nums !important',
    },
  },
  redInput: { '& input': { color: '#ff0000 !important' } },
  sumInputNoBorder: {
    backgroundColor: 'transparent !important', border: 'none !important',
    '& input': { color: '#000 !important', fontWeight: '600 !important', padding: '6px 26px !important' }
  },
  calcInputNoBorder: {
    backgroundColor: 'transparent !important', border: 'none !important',
    '& input': { color: '#718096', fontWeight: '600 !important', padding: '6px 26px !important' }
  },
  rateBadge: {
    padding: '4px 8px', borderRadius: '20px', fontWeight: '600 !important', display: 'inline-block',
    minWidth: '55px', textAlign: 'center', fontSize: '0.8rem'
  },
  errorPulse: {
    animation: '$pulse 1.5s infinite',
    backgroundColor: '#fff5f5 !important', color: '#e53e3e !important', border: '1px solid #feb2b2'
  },
  successBadge: { backgroundColor: '#f0fff4 !important', color: '#38a169 !important', border: '1px solid #c6f6d5' },
  '@keyframes pulse': { '0%': { opacity: 1 }, '50%': { opacity: 0.7 }, '100%': { opacity: 1 } },
  saveBtn: { 
    backgroundColor: '#3182ce !important', color: '#fff !important', borderRadius: '8px !important', 
    padding: '10px 40px !important', fontWeight: '700 !important',
    '&:disabled': { backgroundColor: '#edf2f7 !important', color: '#a0aec0 !important' }
  }
});

const BudgetComponents = ({ 
  deptList, 
  onSave, 
  isLoading, 
  selectedYear, 
  setSelectedYear 
}) => {
  const classes = useStyles();
  
  const [commonTotalBase, setCommonTotalBase] = useState(0);
  const [strategyTotalBase, setStrategyTotalBase] = useState(0);
  const [budgetData, setBudgetData] = useState([]);

  useEffect(() => {
    if (deptList && deptList.length > 0) {
      const formattedData = deptList.map((item) => ({
        id: item.id,
        dept: item.dept,
        direct: item.direct || 0,
        commonRate: item.commonRate || 0,
        strategyRate: item.strategyRate || 0
      }));
      setBudgetData(formattedData);

      const totalSavedCommon = _.sumBy(deptList, 'commonBudget') || 0;
      const totalSavedStrategy = _.sumBy(deptList, 'strategyBudget') || 0;

      setCommonTotalBase(totalSavedCommon);
      setStrategyTotalBase(totalSavedStrategy);
    } else {
      setBudgetData([]);
      setCommonTotalBase(0);
      setStrategyTotalBase(0);
    }
  }, [deptList]);

  const { totalDirect, totalCommonRate, totalStrategyRate } = useMemo(() => ({
    totalDirect: _.sumBy(budgetData, 'direct') || 0,
    totalCommonRate: _.sumBy(budgetData, 'commonRate') || 0,
    totalStrategyRate: _.sumBy(budgetData, 'strategyRate') || 0,
  }), [budgetData]);

  const totalAll = totalDirect + commonTotalBase + strategyTotalBase;

  const handleInputChange = (id, field, value) => {
    const num = Number(value.replace(/[^0-9.]/g, ''));
    setBudgetData(prev => prev.map(item => (item.id === id ? { ...item, [field]: num } : item)));
  };

  const handleSaveClick = () => {
    
    const dataToSave = budgetData
      .filter(row => row.id !== undefined && row.id !== 'total' && row.dept !== '전체 합계')
      .map(row => {
        const calcCommon = commonTotalBase * (row.commonRate / 100)
        const calcStrategy = strategyTotalBase * (row.strategyRate / 100);
        const rowTotal = row.direct + calcCommon + calcStrategy;
        console.log(calcCommon);
        return {
          dptUuid: row.id,
          selectedYear: selectedYear,
          directAmt: row.direct,
          commonRate: row.commonRate,
          strategyRate: row.strategyRate,
          commonBudgetAmt: calcCommon,
          strategyBudgetAmt: calcStrategy,
          totalBudgetAmt: rowTotal
        };
      });

    if (onSave) {
      onSave(dataToSave);
    }
  };

const START_YEAR = 2026; 
const currentYear = new Date().getFullYear();
const yearLength = (currentYear + 1) - START_YEAR + 1;
const yearOptions = Array.from({ length: yearLength }, (_, i) => START_YEAR + i);

  return (
    <Box className={classes.container}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', width: '100%' }}>
        <Typography variant="h6" className={classes.headerTitle} sx={{ mb: 0 }}>
          연간 예산 수립
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <FormControl sx={{ minWidth: 120 }}>
            <InputLabel id="year-select-label" sx={{ fontSize: '1rem' }}>수립년도</InputLabel>
            <Select
              labelId="year-select-label"
              label="수립년도"
              className={classes.yearSelect}
              value={selectedYear || currentYear}
              onChange={(e) => setSelectedYear(e.target.value)}
            >
              {yearOptions.map((y) => (
                <MenuItem key={y} value={y} sx={{ fontSize: '1rem' }}>{y} 년</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button 
            variant="contained" 
            className={classes.saveBtn} 
            onClick={handleSaveClick}
            disabled={totalCommonRate !== 100 || totalStrategyRate !== 100}
            sx={{ 
              height: '36px', 
              width: '50px', 
              padding: '0 16px',
              whiteSpace: 'nowrap',
              fontSize: '0.9rem',
              flexShrink: 0
            }}
          >
            저장
          </Button>
        </Box>
      </Box>
      <Box 
        sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'flex-end', 
          width: '100%',
          minHeight: '40px',
          justifyContent: 'flex-end',
          mb: '8px'
        }}
      >
        <Box sx={{ height: '20px', display: 'flex', alignItems: 'center' }}>
          {(totalCommonRate !== 100 || totalStrategyRate !== 100) && (
            <Typography 
              variant="caption" 
              sx={{ color: '#e53e3e', fontWeight: 700, whiteSpace: 'nowrap', fontSize: '0.75rem' }}
            >
              ※ 배부율 합계가 100%여야 저장이 가능합니다.
            </Typography>
          )}
        </Box>
        <Typography sx={{ fontSize: '0.85rem', color: '#666', height: '20px', display: 'flex', alignItems: 'center' }}>
          (단위: 원)
        </Typography>
      </Box>

      <TableContainer component={Paper} className={classes.tableContainer} elevation={0}>
        <Table className={classes.table} size="small">
          <colgroup>
            <col style={{ width: '150px' }} /><col style={{ width: '160px' }} /><col style={{ width: '160px' }} />
            <col style={{ width: '180px' }} /><col style={{ width: '180px' }} /><col style={{ width: '130px' }} /><col style={{ width: '130px' }} />
          </colgroup>
          <TableHead>
            <TableRow>
              {['부서', '합계', '직접비 예산', '공통비 예산', '전략사업본부 공통비 예산', '공통비 배부율 %', '전략사업본부 배부율 %'].map(h => (
                <TableCell key={h} className={classes.headerCell}>{h}</TableCell>
              ))}
            </TableRow>
            <TableRow className={classes.sumRow}>
              <TableCell align="center">전체 합계</TableCell>
              <TableCell align="right" sx={{ color: '#3182ce', pr: '37px !important' }}>{totalAll.toLocaleString()}</TableCell>
              <TableCell className={classes.inputCell}>
                <InputBase className={`${classes.inputBase} ${classes.sumInputNoBorder}`} value={totalDirect.toLocaleString()} readOnly />
              </TableCell>
              <TableCell className={classes.inputCell}>
                <InputBase className={`${classes.inputBase} ${classes.redInput}`} value={commonTotalBase.toLocaleString()} 
                  onChange={(e) => setCommonTotalBase(Number(e.target.value.replace(/[^0-9]/g, '')))} />
              </TableCell>
              <TableCell className={classes.inputCell}>
                <InputBase className={`${classes.inputBase} ${classes.redInput}`} value={strategyTotalBase.toLocaleString()} 
                  onChange={(e) => setStrategyTotalBase(Number(e.target.value.replace(/[^0-9]/g, '')))} />
              </TableCell>
              <TableCell align="center">
                <Box className={`${classes.rateBadge} ${totalCommonRate !== 100 ? classes.errorPulse : classes.successBadge}`}>{totalCommonRate}%</Box>
              </TableCell>
              <TableCell align="center">
                <Box className={`${classes.rateBadge} ${totalStrategyRate !== 100 ? classes.errorPulse : classes.successBadge}`}>{totalStrategyRate}%</Box>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {budgetData.map((row) => {
              const calcCommon = Math.round(commonTotalBase * (row.commonRate / 100));
              const calcStrategy = Math.round(strategyTotalBase * (row.strategyRate / 100));

              const rowTotal = row.direct + calcCommon + calcStrategy;

              return (
                <TableRow key={row.id}>
                  <TableCell align="center" sx={{ fontWeight: '600 !important' }}>{row.dept}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: '600 !important', pr: '37px !important' }}>{rowTotal.toLocaleString()}</TableCell>
                  <TableCell className={classes.inputCell}>
                    <InputBase className={`${classes.inputBase} ${classes.redInput}`} value={row.direct.toLocaleString()} 
                      onChange={(e) => handleInputChange(row.id, 'direct', e.target.value)} />
                  </TableCell>
                  <TableCell className={classes.inputCell}>
                    <InputBase className={`${classes.inputBase} ${classes.calcInputNoBorder}`} value={calcCommon.toLocaleString()} readOnly />
                  </TableCell>
                  <TableCell className={classes.inputCell}>
                    <InputBase className={`${classes.inputBase} ${classes.calcInputNoBorder}`} value={calcStrategy.toLocaleString()} readOnly />
                  </TableCell>
                  <TableCell align="center">
                    <InputBase className={classes.inputBase} sx={{ '& input': { textAlign: 'center !important', color: '#3182ce' } }} 
                      value={`${row.commonRate}%`} onChange={(e) => handleInputChange(row.id, 'commonRate', e.target.value)} />
                  </TableCell>
                  <TableCell align="center">
                    <InputBase className={classes.inputBase} sx={{ '& input': { textAlign: 'center !important', color: '#805ad5' } }} 
                      value={`${row.strategyRate}%`} onChange={(e) => handleInputChange(row.id, 'strategyRate', e.target.value)} />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default BudgetComponents;
